// Dialer — the TrackDrive callback-dialer admin surface (gated: sec-dialer).
//
// READ + CONFIG only. This page surfaces what the dialer is doing (the dial.*
// outcome funnel, skip reasons, compliance signals, volume/tier/latency, and a
// recent-dials list) and lets an operator edit its operating config + flip it
// on/off. It never touches the dialer's dispatch/gate/DNC logic — those live in
// rtb-api and are structural (the DNC block cannot be defeated by a config knob).
//
// Two data planes, deliberately separate:
//   - LIVE (stats + recent dials): windowed, auto-refreshed every 15s (the
//     MirrorDashboard poll pattern; pauses while the tab is hidden). The window
//     picker defaults to "Today (PT)" — passing no window lets the server use
//     its Pacific-midnight default (api.dialerStats()).
//   - CONFIG (the td_dial_config singleton): loaded once, mutated via the master
//     on/off toggle and the config form. The SmsAutoSend load/field/save pattern.
//
// 🔒 Live-safety: turning the dialer ON starts REAL outbound calls (spend + TCPA),
// so the master toggle requires a deliberate confirm step to enable (Off is
// immediate). The timing band (wait/max_age) sits under an "Advanced" disclosure
// with a client-side guard (max_age must exceed wait) + an incident warning,
// mirroring the server's 400 invalid_band. calls_per_min is capped at the
// dispatcher's per-tick ceiling.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  type DialConfig,
  type DialTierFloor,
  type DialerConfigResponse,
  type DialerDialRow,
  type DialerStats,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  type PillTone,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;
const DIALS_LIMIT = 100;
// Fallback per-tick post ceiling shown before the config (with the real
// tick_cap) loads. calls_per_min may rise to the cap, never past it.
const TICK_CAP_FALLBACK = 60;

// "Today (PT)" is the default selected window and a special sentinel: it sends
// NO window param so the server applies its Pacific-midnight-to-now default
// (api.dialerStats() with no args). The other presets are rolling-window seconds.
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT },
  { label: "Last 15 min", sec: 15 * 60 },
  { label: "Last 30 min", sec: 30 * 60 },
  { label: "Last hour", sec: 3600 },
  { label: "Last 3 hours", sec: 3 * 3600 },
  { label: "Last 6 hours", sec: 6 * 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

// The window params for api.dialerStats — an explicit epoch range, a rolling
// window (seconds), or `undefined` (→ server Pacific-Today default). Matches the
// server's precedence: start/end wins, else window, else the default.
type StatsOpts = { windowSec?: number; start?: number; end?: number };

/** Resolve the selected preset/custom into api.dialerStats opts. */
function toStatsOpts(
  presetSec: number,
  custom: { start: string; end: string } | null,
): StatsOpts | undefined {
  if (custom && custom.start && custom.end) {
    const start = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const end = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { start, end };
  }
  if (presetSec === TODAY_PT) return undefined; // server default = Pacific Today
  return { windowSec: presetSec };
}

function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}

function fmtMs(ms: number | null): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
  return `${Math.round(ms).toLocaleString()} ms`;
}

// Render a dial timestamp (epoch ms) in Pacific, matching the PT reporting model
// used across the dashboards.
function fmtDialTime(ms: number): string {
  if (!ms) return "—";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleString([], {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) + " PT"
  );
}

// A stored tier code → operator-facing label. Unknown values pass through so the
// table never blanks on an unexpected tier.
function prettyTier(tier: string): string {
  switch (tier) {
    case "over_20k":
      return "Over $20k";
    case "over_10k":
      return "Over $10k";
    case "below_10k":
      return "Under $10k";
    default:
      return tier || "—";
  }
}

// Map a dial outcome/status to a Pill tone. Substring-matched so it tolerates
// whatever exact enum value the td-dials row carries (posted / existing_lead /
// rejected_dnc / rejected_dupe / rejected_other / failed / …).
function statusTone(status: string): PillTone {
  const s = status.toLowerCase();
  if (s.includes("dnc")) return "danger";
  if (s.includes("fail")) return "danger";
  if (s.includes("dupe") || s.includes("duplicate")) return "warning";
  if (s.includes("reject")) return "warning";
  if (s.includes("post")) return "success";
  return "neutral";
}

// Friendly labels for the funnel + skip-reason keys.
const FUNNEL_LABELS: Array<{ key: keyof DialerStats["funnel"]; label: string }> = [
  { key: "posted", label: "Posted" },
  { key: "existing_lead", label: "Existing lead" },
  { key: "rejected_dnc", label: "Rejected — DNC" },
  { key: "rejected_dupe", label: "Rejected — duplicate" },
  { key: "rejected_other", label: "Rejected — other" },
  { key: "failed", label: "Failed" },
];

const SKIP_LABELS: Array<{ key: keyof DialerStats["skips"]; label: string }> = [
  { key: "recent_dial", label: "Recent dial (cooldown)" },
  { key: "invalid_phone", label: "Invalid phone" },
  { key: "called_already_local", label: "Already called (local)" },
  { key: "called_already_live", label: "Already called (live)" },
  { key: "probe_failed", label: "DNC probe failed (fail-closed)" },
  { key: "claim_lost", label: "Claim lost (race)" },
  { key: "lead_error", label: "Lead error" },
];

// The editable config fields. Numeric fields are kept as strings so the inputs
// can be cleared mid-edit; they're parsed + validated on Save. `enabled` is NOT
// here — the master toggle owns it; Save carries the current enabled through.
interface FormState {
  tier_floor: DialTierFloor;
  callsPerMin: string;
  waitMin: string;
  maxAgeMin: string;
}

function configToForm(c: DialConfig): FormState {
  return {
    tier_floor: c.tier_floor,
    callsPerMin: String(c.calls_per_min),
    waitMin: String(c.wait_ms / 60000),
    maxAgeMin: String(c.max_age_ms / 60000),
  };
}

export default function Dialer() {
  // Config plane (the td_dial_config singleton + read-only display extras).
  const [config, setConfig] = useState<DialerConfigResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Master on/off toggle state (drives setDialerEnabled).
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [confirmingEnable, setConfirmingEnable] = useState(false);

  // Live plane (stats + recent dials), windowed + polled.
  const [presetSec, setPresetSec] = useState<number>(TODAY_PT); // default: Today (PT)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);
  const [stats, setStats] = useState<DialerStats | null>(null);
  const [dials, setDials] = useState<DialerDialRow[] | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  const statsOpts = useMemo(() => toStatsOpts(presetSec, custom), [presetSec, custom]);

  // --- Config load ---------------------------------------------------------
  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    api
      .dialerConfig()
      .then((r) => {
        // Guard the shape so an edge/API error that fell through to HTML shows a
        // clean message instead of a cryptic undefined-read (the SmsAutoSend fix).
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error("Unexpected dialer config response — try reloading.");
        }
        setConfig(r);
        setForm(configToForm(r.config));
        setConfigError(null);
        setDirty(false);
      })
      .catch((e: unknown) =>
        setConfigError(e instanceof Error ? e.message : "Failed to load config"),
      )
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // --- Live load (stats + recent dials) ------------------------------------
  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLiveLoading(true);
      Promise.all([api.dialerStats(statsOpts), api.dialerDials(DIALS_LIMIT)])
        .then(([s, d]) => {
          setStats(s);
          setDials(d);
          setLiveError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setLiveError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLiveLoading(false));
    },
    [statsOpts],
  );

  // A ref so the poll timer always calls the latest fetch (current window)
  // without re-arming the interval on every filter change.
  const fetchRef = useRef<() => void>(() => {});
  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the window changes.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-refresh polling — pauses when the tab is hidden or auto is toggled off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  // --- Master on/off toggle ------------------------------------------------
  // Off is immediate; On requires the inline confirm (setConfirmingEnable) first.
  const setEnabled = useCallback((next: boolean) => {
    setToggling(true);
    setToggleError(null);
    api
      .setDialerEnabled(next)
      .then((cfg) => {
        setConfig((prev) =>
          prev
            ? { ...prev, config: cfg }
            : { config: cfg, tick_cap: TICK_CAP_FALLBACK, traffic_source_id: null, ad_account: null },
        );
        setConfirmingEnable(false);
      })
      .catch((e: unknown) =>
        setToggleError(e instanceof Error ? e.message : "Failed to update"),
      )
      .finally(() => setToggling(false));
  }, []);

  // --- Config form ---------------------------------------------------------
  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setDirty(true);
  }, []);

  const tickCap = config?.tick_cap ?? TICK_CAP_FALLBACK;

  // Live band guard (mirrors the server's 400 invalid_band): max_age must exceed
  // wait. Only flags when both parse — an empty field is caught by save() instead.
  const waitNum = form ? Number(form.waitMin) : NaN;
  const maxAgeNum = form ? Number(form.maxAgeMin) : NaN;
  const bandInvalid =
    Number.isFinite(waitNum) && Number.isFinite(maxAgeNum) && maxAgeNum <= waitNum;

  const save = useCallback(() => {
    if (!form || !config) return;

    const cpm = Number(form.callsPerMin);
    if (form.callsPerMin.trim() === "" || !Number.isFinite(cpm) || cpm <= 0) {
      setConfigError("Calls/min must be a positive number.");
      return;
    }
    if (cpm > tickCap) {
      setConfigError(`Calls/min is capped at ${tickCap}.`);
      return;
    }
    const wMin = Number(form.waitMin);
    const aMin = Number(form.maxAgeMin);
    if (form.waitMin.trim() === "" || !Number.isFinite(wMin) || wMin <= 0) {
      setConfigError("Wait must be a positive number of minutes.");
      return;
    }
    if (form.maxAgeMin.trim() === "" || !Number.isFinite(aMin) || aMin <= 0) {
      setConfigError("Max age must be a positive number of minutes.");
      return;
    }
    const wait_ms = Math.round(wMin * 60000);
    const max_age_ms = Math.round(aMin * 60000);
    if (max_age_ms <= wait_ms) {
      // Client-side band guard — block the Save before the server 400s.
      setConfigError(
        "Max age must be greater than wait — otherwise the dialer's eligibility band is empty and it dials nobody.",
      );
      return;
    }

    // enabled is NOT edited here: carry the master toggle's current value through
    // so the form never fights the toggle.
    const body: DialConfig = {
      enabled: config.config.enabled,
      tier_floor: form.tier_floor,
      wait_ms,
      max_age_ms,
      calls_per_min: cpm,
    };

    setSaving(true);
    api
      .putDialerConfig(body)
      .then((cfg) => {
        setConfig((prev) =>
          prev
            ? { ...prev, config: cfg }
            : { config: cfg, tick_cap: TICK_CAP_FALLBACK, traffic_source_id: null, ad_account: null },
        );
        setForm(configToForm(cfg));
        setConfigError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) =>
        // The server surfaces invalid_band / invalid_body as the ApiError message.
        setConfigError(e instanceof Error ? e.message : "Save failed"),
      )
      .finally(() => setSaving(false));
  }, [form, config, tickCap]);

  const enabled = config?.config.enabled ?? false;
  const totals = stats?.totals;
  const funnel = stats?.funnel;
  const skips = stats?.skips;
  const latency = stats?.latency;

  // Health hint — the "dark/ticking" line under the master toggle.
  let healthHint: string;
  if (!config) {
    healthHint = "Loading dialer status…";
  } else if (!enabled) {
    healthHint = "Dialer is OFF — no outbound calls are being placed.";
  } else if ((totals?.considered ?? 0) === 0) {
    healthHint = "Dialer is ON but idle — nothing considered in this window yet.";
  } else {
    healthHint = "Dialer is ON and ticking — posting qualified leads to TrackDrive.";
  }

  const presetLabel = custom
    ? "Custom range"
    : PRESETS.find((p) => p.sec === presetSec)?.label ?? "Window";

  const selectStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  return (
    <div>
      <PageHeader
        title="Dialer"
        subtitle="TrackDrive callback dialer — outcomes, recent dials, and operating config"
        actions={
          <div
            style={{
              display: "flex",
              gap: tokens.space.sm,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
              />
              Auto
            </label>
            <button
              type="button"
              onClick={() => load(true)}
              disabled={liveLoading}
              style={buttonStyle("ghost", liveLoading)}
            >
              {liveLoading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginBottom: tokens.space.md,
          }}
        >
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {presetLabel}
        </div>
      )}

      {/* Master on/off toggle — prominent, confirm-on-enable. */}
      <MasterToggle
        loading={configLoading && !config}
        enabled={enabled}
        healthHint={healthHint}
        trafficSourceId={config?.traffic_source_id ?? null}
        adAccount={config?.ad_account ?? null}
        toggling={toggling}
        confirming={confirmingEnable}
        toggleError={toggleError}
        onRequestEnable={() => setConfirmingEnable(true)}
        onConfirmEnable={() => setEnabled(true)}
        onCancelEnable={() => setConfirmingEnable(false)}
        onDisable={() => setEnabled(false)}
      />

      {liveError && !stats && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState title="Failed to load stats" hint={liveError} />
        </Card>
      )}

      {/* Volume + latency stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {liveLoading && !stats ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : totals && latency ? (
          <>
            <StatCard label="Considered" value={fmtCount(totals.considered)} hint="leads evaluated" />
            <StatCard label="Attempted" value={fmtCount(totals.attempted)} hint="posted to TrackDrive" />
            <StatCard label="Posted" value={fmtCount(totals.posted)} hint="accepted as new leads" emphasis />
            <StatCard label="Avg latency" value={fmtMs(latency.avg_ms)} hint="dial post round-trip" />
            <StatCard label="p50 latency" value={fmtMs(latency.p50_ms)} hint="median" />
            <StatCard label="p95 latency" value={fmtMs(latency.p95_ms)} hint="tail" />
          </>
        ) : null}
      </div>

      {/* Funnel / skips / compliance / by-tier breakdowns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        <Card title="Outcome funnel">
          {liveLoading && !funnel ? (
            <Skeleton height={180} />
          ) : funnel ? (
            <Breakdown
              rows={FUNNEL_LABELS.map(({ key, label }) => ({
                label,
                value: funnel[key],
                tone: statusTone(key),
              }))}
            />
          ) : (
            <EmptyState title="No dial outcomes in this window" />
          )}
        </Card>

        <Card title="Skip reasons">
          {liveLoading && !skips ? (
            <Skeleton height={180} />
          ) : skips ? (
            <Breakdown
              rows={SKIP_LABELS.map(({ key, label }) => ({ label, value: skips[key] }))}
            />
          ) : (
            <EmptyState title="No skips in this window" />
          )}
        </Card>

        <Card title="Compliance">
          <p
            style={{
              margin: `0 0 ${tokens.space.md}px`,
              color: tokens.color.fg2,
              fontSize: tokens.font.size.sm,
            }}
          >
            The DNC block + fail-closed probe are structural in the dialer — not
            config knobs. These are the counts they produced this window.
          </p>
          {liveLoading && !funnel ? (
            <Skeleton height={88} />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: tokens.space.md,
              }}
            >
              <StatCard
                label="DNC rejected"
                value={fmtCount(funnel?.rejected_dnc ?? 0)}
                hint="on the do-not-call list"
              />
              <StatCard
                label="Probe fail-closed"
                value={fmtCount(skips?.probe_failed ?? 0)}
                hint="DNC probe errored → skipped"
              />
            </div>
          )}
        </Card>

        <Card title="By debt tier">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_tier.length > 0 ? (
            <Breakdown
              rows={stats.by_tier.map((t) => ({
                label: prettyTier(t.tier),
                value: t.count,
              }))}
            />
          ) : (
            <EmptyState title="No dials by tier in this window" />
          )}
        </Card>
      </div>

      {/* Recent dials table */}
      <Card padding={0} style={{ marginBottom: tokens.space.xl }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: tokens.space.lg,
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: tokens.font.size.md }}>Recent dials</div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
              {dials ? `${dials.length} most recent` : "loading…"}
              {dials && dials.length >= DIALS_LIMIT ? ` (capped at ${DIALS_LIMIT})` : ""}
            </div>
          </div>
        </div>

        {liveLoading && !dials ? (
          <div style={{ padding: tokens.space.lg }}>
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
          </div>
        ) : dials && dials.length === 0 ? (
          <EmptyState title="No dials yet" hint="Recent dials appear here once the dialer posts." />
        ) : dials ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
                whiteSpace: "nowrap",
              }}
            >
              <thead>
                <tr>
                  <Th>Date / time</Th>
                  <Th>Name</Th>
                  <Th>Tier</Th>
                  <Th>Outcome</Th>
                  <Th>TD lead id</Th>
                  <Th>Phone</Th>
                </tr>
              </thead>
              <tbody>
                {dials.map((d, idx) => {
                  const name = `${d.first_name} ${d.last_name}`.trim();
                  return (
                    <tr
                      key={`${d.phone_masked}-${d.dialed_at_ms}-${idx}`}
                      style={{
                        background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                      }}
                    >
                      <Td mono>{fmtDialTime(d.dialed_at_ms)}</Td>
                      <Td>{name || "—"}</Td>
                      <Td>{prettyTier(d.derived_tier)}</Td>
                      <Td>
                        <Pill tone={statusTone(d.status)}>{d.status || "—"}</Pill>
                      </Td>
                      <Td mono>{d.td_lead_id || "—"}</Td>
                      <Td mono>{d.phone_masked || "—"}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {/* Config form */}
      <Card
        title="Operating config"
        action={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {savedAt && (
              <span style={{ color: tokens.color.tealLight, fontSize: tokens.font.size.xs }}>
                Saved{" "}
                {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty || !form}
              style={buttonStyle("primary", saving || !dirty || !form)}
            >
              {saving ? "Saving…" : dirty ? "Save" : "Saved"}
            </button>
          </div>
        }
        style={{ marginBottom: tokens.space.xl }}
      >
        {configError && (
          <div
            role="alert"
            style={{
              marginBottom: tokens.space.lg,
              padding: `${tokens.space.sm}px ${tokens.space.md}px`,
              borderRadius: tokens.radius.sm,
              background: tokens.color.dangerBg,
              border: `1px solid ${tokens.color.dangerBorder}`,
              color: tokens.color.danger,
              fontSize: tokens.font.size.sm,
            }}
          >
            {configError}
          </div>
        )}

        {configLoading && !form ? (
          <>
            <Skeleton height={20} width={160} />
            <div style={{ height: tokens.space.md }} />
            <Skeleton height={36} />
            <div style={{ height: tokens.space.md }} />
            <Skeleton height={36} />
          </>
        ) : form ? (
          <>
            <p
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                marginTop: 0,
              }}
            >
              These knobs are the dialer's real control surface — saved to the
              config the live dialer reads each tick. The on/off master switch is
              at the top of the page.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: tokens.space.lg,
                alignItems: "start",
              }}
            >
              {/* Tier floor */}
              <div>
                <FieldLabel
                  htmlFor="dialer-tier-floor"
                  hint="the lowest debt tier we're willing to dial"
                >
                  Tier floor
                </FieldLabel>
                <select
                  id="dialer-tier-floor"
                  value={form.tier_floor}
                  onChange={(e) => update("tier_floor", e.target.value as DialTierFloor)}
                  style={{ ...inputStyle, cursor: "pointer" }}
                  aria-label="Tier floor"
                >
                  <option value="over_20k">Over $20k only</option>
                  <option value="over_10k">Over $10k (incl. over $20k)</option>
                </select>
              </div>

              {/* Calls per minute */}
              <div>
                <FieldLabel
                  htmlFor="dialer-calls-per-min"
                  hint={`sends per tick — capped at ${tickCap}`}
                >
                  Calls / min
                </FieldLabel>
                <input
                  id="dialer-calls-per-min"
                  type="number"
                  min="1"
                  max={tickCap}
                  value={form.callsPerMin}
                  onChange={(e) => update("callsPerMin", e.target.value)}
                  style={inputStyle}
                  aria-label="Calls per minute"
                />
              </div>
            </div>

            {/* Advanced disclosure — the timing band (guarded). */}
            <div style={{ marginTop: tokens.space.lg }}>
              <AdvancedBand
                waitMin={form.waitMin}
                maxAgeMin={form.maxAgeMin}
                bandInvalid={bandInvalid}
                onWaitChange={(v) => update("waitMin", v)}
                onMaxAgeChange={(v) => update("maxAgeMin", v)}
              />
            </div>
          </>
        ) : null}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Master on/off toggle — prominent status + a deliberate confirm to enable.
// Off is immediate (no confirm); On reveals the warning + confirm buttons.
// ---------------------------------------------------------------------------

function MasterToggle({
  loading,
  enabled,
  healthHint,
  trafficSourceId,
  adAccount,
  toggling,
  confirming,
  toggleError,
  onRequestEnable,
  onConfirmEnable,
  onCancelEnable,
  onDisable,
}: {
  loading: boolean;
  enabled: boolean;
  healthHint: string;
  trafficSourceId: string | null;
  adAccount: string | null;
  toggling: boolean;
  confirming: boolean;
  toggleError: string | null;
  onRequestEnable: () => void;
  onConfirmEnable: () => void;
  onCancelEnable: () => void;
  onDisable: () => void;
}) {
  return (
    <Card
      style={{
        marginBottom: tokens.space.xl,
        borderColor: enabled ? "rgba(0,212,170,0.28)" : tokens.color.border,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: tokens.space.lg,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.md,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: tokens.font.size.lg,
                color: tokens.color.fg0,
              }}
            >
              Outbound dialer
            </span>
            {loading ? (
              <Pill tone="neutral">…</Pill>
            ) : enabled ? (
              <Pill tone="success">On</Pill>
            ) : (
              <Pill tone="neutral">Off</Pill>
            )}
          </div>
          <p
            style={{
              margin: `${tokens.space.sm}px 0 0`,
              color: tokens.color.fg1,
              fontSize: tokens.font.size.sm,
            }}
          >
            {healthHint}
          </p>
          {(trafficSourceId || adAccount) && (
            <p
              style={{
                margin: `${tokens.space.xs}px 0 0`,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.xs,
              }}
            >
              Posts under
              {trafficSourceId ? ` traffic_source_id ${trafficSourceId}` : ""}
              {trafficSourceId && adAccount ? " ·" : ""}
              {adAccount ? ` ad_account ${adAccount}` : ""} (read-only)
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
          {toggling ? (
            <button type="button" disabled style={buttonStyle("ghost", true)}>
              Working…
            </button>
          ) : enabled ? (
            <button
              type="button"
              onClick={onDisable}
              aria-label="Turn the dialer off"
              style={buttonStyle("danger")}
            >
              Turn Off
            </button>
          ) : confirming ? (
            <>
              <button
                type="button"
                onClick={onConfirmEnable}
                aria-label="Confirm — start live dialing"
                style={buttonStyle("primary")}
              >
                Yes, start live dialing
              </button>
              <button type="button" onClick={onCancelEnable} style={buttonStyle("ghost")}>
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onRequestEnable}
              aria-label="Turn the dialer on"
              disabled={loading}
              style={buttonStyle("primary", loading)}
            >
              Turn On
            </button>
          )}
        </div>
      </div>

      {confirming && !enabled && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.lg,
            padding: `${tokens.space.md}px ${tokens.space.lg}px`,
            borderRadius: tokens.radius.md,
            background: tokens.color.dangerBg,
            border: `1px solid ${tokens.color.dangerBorder}`,
            color: tokens.color.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          <strong>Are you sure — this starts live dialing.</strong> Turning the
          dialer on places REAL outbound calls (ad spend + TCPA exposure). Leads
          matching your tier floor and timing band will be posted to TrackDrive on
          the next tick. Off is immediate; on is deliberate.
        </div>
      )}

      {toggleError && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.md,
            color: tokens.color.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          {toggleError}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Advanced disclosure — the eligibility band (wait / max_age) in MINUTES, with
// the incident warning + the client-side max_age>wait guard. Collapsed by
// default so the common config (tier + rate) stays uncluttered.
// ---------------------------------------------------------------------------

function AdvancedBand({
  waitMin,
  maxAgeMin,
  bandInvalid,
  onWaitChange,
  onMaxAgeChange,
}: {
  waitMin: string;
  maxAgeMin: string;
  bandInvalid: boolean;
  onWaitChange: (v: string) => void;
  onMaxAgeChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.md,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: tokens.space.sm,
          padding: `${tokens.space.md}px ${tokens.space.lg}px`,
          background: "transparent",
          border: "none",
          color: tokens.color.fg0,
          fontSize: tokens.font.size.base,
          fontWeight: 600,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span>Advanced — timing band</span>
        <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div
          style={{
            padding: `0 ${tokens.space.lg}px ${tokens.space.lg}px`,
            display: "flex",
            flexDirection: "column",
            gap: tokens.space.md,
          }}
        >
          <div
            role="note"
            style={{
              padding: `${tokens.space.sm}px ${tokens.space.md}px`,
              borderRadius: tokens.radius.sm,
              background: "rgba(212,168,83,0.12)",
              border: "1px solid rgba(212,168,83,0.40)",
              color: tokens.color.warning,
              fontSize: tokens.font.size.xs,
            }}
          >
            Widening max age can dial stale leads — a past incident dialed 51
            people ~54 minutes late. Keep the band tight and change it
            deliberately.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel
                htmlFor="dialer-wait-min"
                hint="minutes to wait after a lead pulls before it's eligible"
              >
                Wait (minutes)
              </FieldLabel>
              <input
                id="dialer-wait-min"
                type="number"
                min="1"
                step="1"
                value={waitMin}
                onChange={(e) => onWaitChange(e.target.value)}
                style={inputStyle}
                aria-label="Wait (minutes)"
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="dialer-max-age-min"
                hint="minutes after the pull past which a lead is too stale to dial"
              >
                Max age (minutes)
              </FieldLabel>
              <input
                id="dialer-max-age-min"
                type="number"
                min="1"
                step="1"
                value={maxAgeMin}
                onChange={(e) => onMaxAgeChange(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: bandInvalid
                    ? tokens.color.dangerBorder
                    : tokens.color.borderMid,
                }}
                aria-label="Max age (minutes)"
              />
            </div>
          </div>

          {bandInvalid && (
            <div
              role="alert"
              style={{ color: tokens.color.danger, fontSize: tokens.font.size.xs }}
            >
              Max age must be greater than wait — the band is empty as entered, so
              the dialer would dial nobody. Fix this before saving.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown — a compact label → count list used for the funnel, skip reasons,
// and by-tier cards. An optional tone tints the count (funnel outcomes).
// ---------------------------------------------------------------------------

function Breakdown({
  rows,
}: {
  rows: Array<{ label: string; value: number; tone?: PillTone }>;
}) {
  const toneColor: Record<PillTone, string> = {
    neutral: tokens.color.fg0,
    success: tokens.color.tealLight,
    warning: tokens.color.warning,
    danger: tokens.color.danger,
  };
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.space.md,
            padding: `${tokens.space.sm}px 0`,
            borderBottom:
              i < rows.length - 1 ? `1px solid ${tokens.color.border}` : "none",
          }}
        >
          <span style={{ color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
            {r.label}
          </span>
          <span
            style={{
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              color: r.tone ? toneColor[r.tone] : tokens.color.fg0,
            }}
          >
            {fmtCount(r.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared table + skeleton atoms (mirrors MirrorDashboard).
// ---------------------------------------------------------------------------

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right" | "center";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
